/**
 * NotificationItem Component - APB-1296
 *
 * Epic: APB-1306 - [Notification] EPIC-2 - Approval Workflow Notifications (P1)
 *
 * Individual notification item displayed in the notification center dropdown.
 * Supports interactive actions, read/unread status, and navigation.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '../../shared/services/notificationsApi';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onClose: () => void;
}

/**
 * Get icon SVG based on notification type
 */
const getNotificationIcon = (type: string): JSX.Element => {
  switch (type) {
    case 'approval_required':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'action_approved':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'action_rejected':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    case 'alert_critical':
    case 'alert_update':
    case 'alert_assigned':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case 'anomaly_detected':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case 'escalation':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

/**
 * Get background color classes based on priority
 */
const getPriorityColors = (priority: string, isRead: boolean): string => {
  if (isRead) {
    return 'bg-gray-800/50';
  }

  switch (priority) {
    case 'critical':
      return 'bg-red-900/30 border-l-4 border-red-500';
    case 'high':
      return 'bg-orange-900/30 border-l-4 border-orange-500';
    case 'medium':
      return 'bg-blue-900/30 border-l-4 border-blue-500';
    case 'low':
      return 'bg-gray-800';
    default:
      return 'bg-gray-800';
  }
};

/**
 * Get icon color classes based on priority
 */
const getIconColor = (priority: string): string => {
  switch (priority) {
    case 'critical':
      return 'text-red-400';
    case 'high':
      return 'text-orange-400';
    case 'medium':
      return 'text-blue-400';
    case 'low':
      return 'text-gray-400';
    default:
      return 'text-gray-400';
  }
};

/**
 * Format relative time
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDismiss,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    // Mark as read on click
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    // Navigate based on action_payload or related entities
    if (notification.action_payload && notification.action_payload.length > 0) {
      const navigateAction = notification.action_payload.find(a => a.action === 'navigate');
      if (navigateAction?.url) {
        onClose();
        navigate(navigateAction.url);
        return;
      }
    }

    // Fallback to alert or action pages
    if (notification.action_id) {
      onClose();
      navigate(`/soar/actions/${notification.action_id}`);
    } else if (notification.alert_id) {
      onClose();
      navigate(`/alerts/${notification.alert_id}`);
    }
  };

  const handleAction = (e: React.MouseEvent, action: { action: string; url?: string; params?: Record<string, unknown> }) => {
    e.stopPropagation();

    if (action.action === 'navigate' && action.url) {
      onClose();
      navigate(action.url);
    } else if (action.action === 'approve' && action.params?.action_id) {
      onClose();
      navigate(`/soar/actions/${action.params.action_id}?action=approve`);
    } else if (action.action === 'reject' && action.params?.action_id) {
      onClose();
      navigate(`/soar/actions/${action.params.action_id}?action=reject`);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss(notification.id);
  };

  return (
    <div
      onClick={handleClick}
      className={`
        ${getPriorityColors(notification.priority, notification.is_read)}
        px-4 py-3 cursor-pointer hover:bg-gray-700/50 transition-colors
        ${notification.is_read ? 'opacity-75' : ''}
      `}
    >
      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className={`flex-shrink-0 mt-0.5 ${getIconColor(notification.priority)}`}>
          {getNotificationIcon(notification.notification_type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <p className={`text-sm font-medium ${notification.is_read ? 'text-gray-400' : 'text-white'}`}>
            {notification.title}
          </p>

          {/* Message */}
          <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
            {notification.message}
          </p>

          {/* Actions */}
          {notification.action_payload && notification.action_payload.length > 0 && !notification.is_read && (
            <div className="flex items-center space-x-2 mt-2">
              {notification.action_payload.slice(0, 3).map((action, idx) => (
                <button
                  key={idx}
                  onClick={(e) => handleAction(e, action)}
                  className={`
                    text-xs px-2 py-1 rounded
                    ${action.action === 'approve'
                      ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                      : action.action === 'reject'
                        ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }
                    transition-colors
                  `}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Timestamp and source */}
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">
              {formatRelativeTime(notification.created_at)}
            </span>
            {notification.source_service && (
              <span className="text-xs text-gray-600 uppercase tracking-wide">
                {notification.source_service.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 text-gray-500 hover:text-gray-300 rounded hover:bg-gray-700 transition-colors"
          title="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Unread indicator */}
        {!notification.is_read && (
          <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2" />
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
